import { FC } from 'react'
import { ChevronRight } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta } from './types'
import type { RunningHourCounterDto } from '../../../core/pms/_models'

interface Props {
  counters: RunningHourCounterDto[]
  onViewAll: () => void
}

const meta: CardMeta = {
  description: 'Running hour counters for equipment. Shows current value in hours and last reading date.',
  endpoint: 'GET /pms/running-hours/counters',
  logic: 'Fetches running hour counters scoped to the user\'s vessel (single-vessel users only). Shows top 12 counters.',
}

const fmtDate = (d?: string | null) => {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

const RunningHourGauges: FC<Props> = ({ counters, onViewAll }) => {
  if (counters.length === 0) return null

  return (
    <div className='bg-white rounded-2xl p-4 border border-slate-200 shadow-sm'>
      <div className='flex items-center justify-between mb-3'>
        <h3 className='text-sm font-bold text-slate-900'>
          Running Hours Counters <span className='text-slate-400 font-normal'>({counters.length})</span>
        </h3>
        <div className='flex items-center gap-2'>
          <CardInfoButton meta={meta} />
          <button onClick={onViewAll} className='text-[11px] text-blue-600 hover:underline flex items-center gap-0.5'>
            View all <ChevronRight className='w-3 h-3' />
          </button>
        </div>
      </div>
      <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3'>
        {counters.slice(0, 12).map((c) => (
          <div
            key={c.id}
            onClick={onViewAll}
            className='bg-slate-50 rounded-xl p-3 border border-slate-100 hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all'
          >
            <div className='text-[10px] text-slate-500 truncate' title={c.equipmentName || c.name}>
              {c.equipmentName || c.name}
            </div>
            <div className='text-lg font-bold text-slate-900 mt-0.5'>
              {c.currentValue.toLocaleString()}<span className='text-[10px] font-normal text-slate-400 ml-0.5'>hrs</span>
            </div>
            <div className='text-[9px] text-slate-400 mt-0.5'>
              {c.lastReadingDate ? fmtDate(c.lastReadingDate) : 'No reading'}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default RunningHourGauges
